import React, { forwardRef, useImperativeHandle, useRef } from 'react';

import { isRecaptchaEnabled } from './constants';
import { useRecaptchaScaleToFit } from './hooks/useRecaptchaScaleToFit';
import { useRecaptchaWidget } from './hooks/useRecaptchaWidget';
import type { ReCaptchaV2Handle } from './types';

export type { ReCaptchaV2Handle } from './types';

interface ReCaptchaV2Props {
  onChange: (token: string | null) => void;
}

export const ReCaptchaV2 = forwardRef<ReCaptchaV2Handle, ReCaptchaV2Props>(
  ({ onChange }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const { wrapperRef, scaleRef, updateScale, baseWidth, baseHeight } =
      useRecaptchaScaleToFit({ enabled: isRecaptchaEnabled });

    const { reset, getResponse } = useRecaptchaWidget({
      containerRef,
      onChange,
      onRendered: updateScale,
    });

    useImperativeHandle(ref, () => ({ reset, getResponse }), [
      getResponse,
      reset,
    ]);

    if (!isRecaptchaEnabled) return null;

    return (
      <div ref={wrapperRef} className="w-full overflow-hidden">
        <div ref={scaleRef} style={{ width: baseWidth, height: baseHeight }}>
          <div ref={containerRef} data-test-id="recaptcha-v2" />
        </div>
      </div>
    );
  }
);

ReCaptchaV2.displayName = 'ReCaptchaV2';
